import React, { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Modal, Table } from 'src/components';
import { fetchBilling } from 'src/redux/action/billing.action';
import { RootState } from 'src/redux/reducers';
import { formatDateNext } from '../kds';
import { CurrencyAmount } from 'src/containers/currency';

interface IProps {}

type Filters = {
  page: number;
  limit: number;
};

type InfoRowProps = {
  label: string;
  value?: React.ReactNode;
};

const Billing: React.FC<IProps> = ({}) => {
  const dispatch = useDispatch();
  const initialFilters: Filters = {
    page: 1,
    limit: 5,
  };
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const { billingData, billingPagination, loading } = useSelector(
    ({ billingData }: RootState) => billingData,
    shallowEqual
  );
  const [details, setDetails] = React.useState<any>(null);

  React.useEffect(() => {
    dispatch(fetchBilling(filters) as any);
  }, [dispatch, filters]);

  // table column
  const tableColumn = [
    {
      Header: 'Date',
      Cell: (row: any) => (
        <div className="flex flex-col text-light-grey gap-1">
          <p>Start:{formatDateNext(row?.cell?.row?.original?.billingPeriodStart)}</p>
          <p>End: {formatDateNext(row?.cell?.row?.original?.billingPeriodEnd)}</p>
        </div>
      ),
    },
    {
      Header: 'Invoice Number',
      accessor: 'invoiceNumber',
      width: 300
    },
    { 
      Header: 'Amount',
      Cell: (row: any) => (
        <div className="flex flex-col text-light-grey gap-1">
          <CurrencyAmount amount={row?.cell?.row?.original?.totalCharge}/>
        </div>
      ),
    },
    { 
      Header: 'Status',
      Cell: (row: any) => (
        <div className="">
          {row?.cell?.row?.original?.isPaid ? 'Paid' : 'Not paid'}
        </div>
      )
     },
  ];

  const handleShowDetails = (row: any) => {
    setDetails(row.original)
  }
  const handleCloseModal = () => {
    setDetails(null);
  };
  const detailsInfoLeft = details ? [
  {
    label: 'Name',
    value: details.businessSnapshot?.businessName,
  },
  {
    label: 'Invoice number',
    value: details.invoiceNumber,
  },
  {
    label: 'Cycle Month',
    value: details.cycleMonth,
  },
  {
    label: 'Billing Period Start',
    value: formatDateNext(details.billingPeriodStart),
  },
  {
    label: 'Billing Period End',
    value: formatDateNext(details.billingPeriodEnd),
  },
  {
    label: 'Payment Status',
    value: details.isPaid ? 'Paid' : 'Not paid',
  },
  ] : [];
  const detailsInfoRight = details ? [
  {
    label: 'Free order quota',
    value: details.freeOrderQuota,
  },
  {
    label: 'Free orders',
    value: details.freeOrders,
  },
  {
    label: 'Paid orders',
    value: details.paidOrders,
  },
  {
    label: 'Price per order',
    value: details.pricePerOrder,
  },
  {
    label: 'Total orders',
    value: details.totalOrders,
  },
  {
    label: 'Subtotal',
    value: details.subtotalAmount,
  },
  {
    label: 'Total charge',
    value: details.totalCharge,
  },
  ]: [];
  const InfoRow = ({ label, value }: InfoRowProps) => (
    <div className="grid grid-cols-2 items-center gap-2">
      <div className="font-semibold text-base text-light-grey">{label}</div>
      <div className="text-sm">{value ?? '-'}</div>
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <Table
        loading={loading}
        tableColumn={tableColumn}
        tableData={billingData}
        pagination={true}
        className='mt-0'
        serverPagination={{
          pageIndex: filters.page - 1, // 0-based index for table
          pageSize: filters.limit,
          totalPages: billingPagination.totalPages,
          setPageIndex: (page: number) =>
            setFilters((prev) => ({ ...prev, page: page + 1 })), // back to 1-based
          setPageSize: (limit: number) =>
            setFilters((prev) => ({ ...prev, limit, page: 1 })), // reset to page 1
        }}
        onSelectRow={handleShowDetails}
      />
      {details && <Modal
        title={'Billing details'}
        onClick={handleCloseModal}
        className="w-[60vw] z-[9988]"
      >
        <div className="mt-4 grid grid-cols-2 gap-8">
          <div className="flex flex-col gap-4">
            {detailsInfoLeft.map((item) => (
              <InfoRow key={item.label} {...item} />
            ))}
          </div>

          <div className="flex flex-col gap-4">
            {detailsInfoRight.map((item) => (
              <InfoRow key={item.label} {...item} />
            ))}
          </div>
        </div>
        <div className="">
          <div className="...">Notes</div>
          <div className="...">{details.notes}</div>
        </div>
      </Modal>}
    </div>
  );
};

export default React.memo(Billing);